import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Calculator, CheckCircle2, MessageSquare, Phone, Send, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

export default function PropertyValuation() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    ilce: "Seyhan",
    mahalle: "Gürselpaşa",
    propertyType: "daire",
    netArea: "",
    buildingAge: "0-3 Yaş"
  });

  const handleValuationSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "generate_lead", {
        event_category: "Valuation",
        event_label: "Property Valuation Form"
      });
    }

    toast({
      title: "WhatsApp mesajınız hazırlanıyor",
      description: "Talebinizi iletmek için açılan WhatsApp penceresinde mesajı gönderin.",
    });

    const waText = encodeURIComponent(
      `Merhaba Corner Gayrimenkul, Adana'daki evimin değerini öğrenmek istiyorum.\n\n` +
      `Ad Soyad: ${formData.fullName}\n` +
      `Telefon: ${formData.phone}\n` +
      `Konum: Adana / ${formData.ilce} / ${formData.mahalle}\n` +
      `Tip: ${formData.propertyType}\n` +
      `Net m²: ${formData.netArea}\n` +
      `Bina Yaşı: ${formData.buildingAge}`
    );

    window.open(`https://wa.me/905335487801?text=${waText}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Adana Evimin Değeri Ne Kadar? | Ücretsiz Gayrimenkul Değerleme"
        description="Adana Seyhan, Çukurova ve Gürselpaşa bölgelerindeki evinizin piyasa değerini ücretsiz öğrenin. Corner Gayrimenkul profesyonel ekspertiz ve değerleme analizi."
        canonicalPath="/degerleme"
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Ücretsiz Ev Değerleme" }]} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center my-8">
          {/* Form */}
          <Card className="p-6 sm:p-8 bg-card border border-border shadow-xl rounded-3xl order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                <Calculator className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-foreground">
                  Ücretsiz Değerleme Formu
                </h3>
                <p className="text-xs text-muted-foreground">Evinizin güncel piyasa değerini öğrenin</p>
              </div>
            </div>

            <form onSubmit={handleValuationSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Ad Soyad *</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="Örn: Mehmet Öz"
                  className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Telefon *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Telefon numaranız"
                  className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">İlçe</label>
                  <select
                    value={formData.ilce}
                    onChange={(e) => setFormData({ ...formData, ilce: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  >
                    <option value="Seyhan">Seyhan</option>
                    <option value="Çukurova">Çukurova</option>
                    <option value="Sarıçam">Sarıçam</option>
                    <option value="Yüreğir">Yüreğir</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Mahalle</label>
                  <input
                    type="text"
                    value={formData.mahalle}
                    onChange={(e) => setFormData({ ...formData, mahalle: e.target.value })}
                    placeholder="Örn: Gürselpaşa"
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Yaklaşık Net m²</label>
                  <input
                    type="number"
                    value={formData.netArea}
                    onChange={(e) => setFormData({ ...formData, netArea: e.target.value })}
                    placeholder="Örn: 165"
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Bina Yaşı</label>
                  <select
                    value={formData.buildingAge}
                    onChange={(e) => setFormData({ ...formData, buildingAge: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm"
                  >
                    <option value="0-3 Yaş">0-3 Yaş</option>
                    <option value="4-10 Yaş">4-10 Yaş</option>
                    <option value="11+ Yaş">11+ Yaş</option>
                  </select>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full font-bold text-base shadow-lg">
                <Send className="h-4 w-4 mr-2" /> Değerleme Raporu İste
              </Button>
            </form>
          </Card>

          {/* Intro Text */}
          <div className="space-y-6 order-1 lg:order-2">
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
              Emsal & Piyasa Analizi
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
              Adana'da Evimin Piyasa Fiyatı Ne Kadar?
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Gayrimenkulünüzün gerçek satış değerini yanlış tahmin etmek aylarca alıcı bulamamaya veya zararına satmaya sebep olabilir. Corner Gayrimenkul olarak Adana Seyhan ve Çukurova bölgelerindeki güncel emsal verileriyle ücretsiz ekspertiz sunuyoruz.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div className="p-4 rounded-xl bg-card border border-border">
                <TrendingUp className="h-6 w-6 text-primary mb-2" />
                <h4 className="font-bold text-base text-foreground">Gerçek Emsal Analizi</h4>
                <p className="text-xs text-muted-foreground mt-1">Bölgenizde son satılan benzer dairelerin gerçek gerçekleşen satış rakamlarıyla kıyaslıyoruz.</p>
              </div>

              <div className="p-4 rounded-xl bg-card border border-border">
                <CheckCircle2 className="h-6 w-6 text-green-600 mb-2" />
                <h4 className="font-bold text-base text-foreground">%100 Ücretsiz & Taahhütsüz</h4>
                <p className="text-xs text-muted-foreground mt-1">Değerleme raporumuz tamamıyla ücretsiz olup herhangi bir sözleşme zorunluluğu barındırmaz.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
